// Tiny wrapper around Supabase Realtime postgres_changes — just enough for
// the dashboard and account-detail page to subscribe to inserts on a single
// table without duplicating channel setup/teardown.
//
// IMPORTANT: realtime must be enabled per-table in the Supabase dashboard
// (Database → Replication → toggle on each table). Without that, the channel
// connects fine but no events are ever emitted.
//
// RLS still applies. Subscribers only receive rows their JWT permits via
// the SELECT policy on the table — same as a regular SELECT query. Customer
// users get filtered automatically; admins get everything.

import { useEffect, useRef } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from './supabase';

type InsertHandler<T> = (row: T) => void;

/**
 * Subscribe to INSERTs on a single table. The handler is called with the
 * `new` row from each event. `filter` uses PostgREST syntax, e.g.
 * `account_login=eq.37313754` — passed straight through to Supabase.
 *
 * The handler is stored in a ref so changing it across renders does NOT
 * teardown/recreate the channel (subscribing is moderately expensive).
 */
export function useRealtimeInserts<T>(
  schema: string,
  table: string,
  filter: string | undefined,
  onInsert: InsertHandler<T>,
  enabled = true,
) {
  const cb = useRef(onInsert);
  useEffect(() => { cb.current = onInsert; });

  useEffect(() => {
    if (!enabled) return;
    const channelName = `rt:${schema}:${table}:${filter ?? 'all'}`;
    const ch: RealtimeChannel = supabase
      .channel(channelName)
      // postgres_changes is the canonical event for table-level CDC.
      // We only listen for INSERT here; UPDATE/DELETE on snapshots/trades
      // is rare in this product (rows are append-only).
      .on(
        // Type assertion: the supabase-js types for `on` are overloaded and
        // the postgres_changes path uses a string literal that the compiler
        // can't reconcile with the broader Channel type without ceremony.
        // The runtime contract is well-defined.
        'postgres_changes' as 'system',
        { event: 'INSERT', schema, table, ...(filter ? { filter } : {}) } as never,
        (payload: { new: T }) => cb.current(payload.new),
      )
      .subscribe();

    return () => { supabase.removeChannel(ch); };
  }, [schema, table, filter, enabled]);
}

/** Tiny debounce — used by the dashboard to coalesce bursts of inserts
 *  into one re-fetch. */
export function debounced<F extends (...args: never[]) => void>(fn: F, ms: number): F {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return ((...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as F;
}
